/**
 * <Article80ClassifyWizard /> — M3X.EOS F.6 KEYSTONE.
 *
 * HR writes the termination narrative; Sonnet classifies into
 * one of 11 Article 80 subtypes; HR has final say (accept /
 * override / reject Article 80 entirely).
 *
 * Renders 3 panels:
 *   1. Narrative input (with AI-classify button)
 *   2. AI suggestion card (confidence-tinted) + candidates
 *   3. HR final decision (accept / override / reject)
 *
 * Mode-invariant for Mutaba'aa cross-vetoes (Article 81 / 152 / 90)
 * — those fire equally in every mode at termination submit-time.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type Article80ClassifyResult } from "../../lib/api";

export function Article80ClassifyWizard({
  initialNarrative = "",
  onConfirm,
}: {
  initialNarrative?: string;
  onConfirm: (subtypeCode: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";

  const [narrative, setNarrative] = useState(initialNarrative);
  const [result, setResult] = useState<Article80ClassifyResult | null>(null);
  const [overrideCode, setOverrideCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function classify() {
    if (!narrative.trim()) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await api.classifyArticle80(narrative);
      setResult(r);
      setOverrideCode(r.suggested_subtype_code);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }

  const confidence = result ? Number(result.confidence) : 0;
  const confidenceTone =
    confidence >= 0.85
      ? "emerald"
      : confidence >= 0.55
        ? "amber"
        : "rose";

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-surface-default p-5">
      <div>
        <h3 className="text-base font-semibold text-text-primary">
          {t(
            "article_80.title",
            "Article 80 — Termination With Cause Classifier",
          )}
        </h3>
        <p className="mt-1 text-xs text-text-tertiary">
          {t(
            "article_80.subtitle",
            "Tashghil classifies your narrative into one of 11 subtypes. You have final say.",
          )}
        </p>
      </div>

      {/* Panel 1: Narrative */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-secondary">
          {t("article_80.narrative_label", "HR Narrative")}
        </label>
        <textarea
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          rows={4}
          placeholder={t(
            "article_80.narrative_placeholder",
            "Describe the termination case — what happened, when, evidence available…",
          )}
          className="w-full rounded-md border border-border-default bg-surface-default p-2 text-sm"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={classify}
            disabled={loading || !narrative.trim()}
            className="rounded-md bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-blue/90 disabled:opacity-50"
          >
            {loading
              ? t("article_80.classifying", "Classifying…")
              : t("article_80.classify_btn", "✦ Classify with Tashghil")}
          </button>
        </div>
        {err && (
          <p className="mt-2 rounded bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
            {err}
          </p>
        )}
      </div>

      {/* Panel 2: AI suggestion */}
      {result && (
        <div
          className={`rounded-md border p-4 ${
            confidenceTone === "emerald"
              ? "border-emerald-200 bg-emerald-50"
              : confidenceTone === "amber"
                ? "border-amber-200 bg-amber-50"
                : "border-rose-200 bg-rose-50"
          }`}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-text-tertiary">
              ✦ {t("article_80.tashghil_suggests", "Tashghil suggests")}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                confidenceTone === "emerald"
                  ? "bg-emerald-200 text-emerald-900"
                  : confidenceTone === "amber"
                    ? "bg-amber-200 text-amber-900"
                    : "bg-rose-200 text-rose-900"
              }`}
            >
              {Math.round(confidence * 100)}% {t("article_80.confidence", "confidence")}
            </span>
          </div>
          <div className="text-sm font-medium text-text-primary">
            {isAr
              ? result.suggested_subtype.label_ar
              : result.suggested_subtype.label_en}
          </div>
          <div className="mt-1 text-[10px] font-mono text-text-tertiary">
            {result.suggested_subtype_code} ·{" "}
            {t("article_80.source", "source")}: {result.source}
          </div>
          <p className="mt-2 text-xs text-text-secondary">{result.reasoning}</p>

          {result.candidate_subtypes.length > 1 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-brand-blue">
                {t(
                  "article_80.show_candidates",
                  "Other candidates",
                )}{" "}
                ({result.candidate_subtypes.length - 1})
              </summary>
              <ul className="mt-2 space-y-1 text-xs">
                {result.candidate_subtypes.slice(1).map((c) => (
                  <li key={c.code} className="flex justify-between">
                    <span>{isAr ? c.label_ar : c.label_en}</span>
                    <span className="font-mono text-text-tertiary">
                      {Math.round(Number(c.confidence) * 100)}%
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="mt-3 border-t border-current/20 pt-3">
            <p className="text-xs font-semibold text-text-secondary">
              {t(
                "article_80.evidence_required",
                "Evidence Mutaba'aa will require:",
              )}
            </p>
            <ul className="mt-1 list-disc ps-5 text-xs text-text-secondary">
              {result.evidence_required.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Panel 3: HR final say */}
      {result && (
        <div className="rounded-md border border-slate-200 bg-surface-subtle p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
            {t("article_80.hr_final_say", "HR Final Decision")}
          </p>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={overrideCode === result.suggested_subtype_code}
                onChange={() =>
                  setOverrideCode(result.suggested_subtype_code)
                }
              />
              <span>
                {t("article_80.accept_ai", "Accept Tashghil's suggestion")}
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={overrideCode === "override"}
                onChange={() => setOverrideCode("override")}
              />
              <span>{t("article_80.override", "Override to different subtype")}</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={overrideCode === null}
                onChange={() => setOverrideCode(null)}
              />
              <span>
                {t(
                  "article_80.reject",
                  "Reject Article 80 — termination without cause (full gratuity)",
                )}
              </span>
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() =>
                onConfirm(
                  overrideCode === "override"
                    ? null
                    : overrideCode,
                )
              }
              className="rounded-md bg-brand-deep px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-deep/90"
            >
              {t("article_80.confirm", "Confirm classification")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Article80ClassifyWizard;
